import * as React from 'react';
import { Row } from 'react-bootstrap';
import type { WrappedFieldArrayProps } from 'redux-form';
import { Field } from 'redux-form';

import type FieldWorkClassificationCodeListItemVM from 'ab-viewModels/fieldWorkClassificationCode/listItem';
import type { EquipmentListItemVM } from 'ab-viewModels/timeSplitEquipment/timeSplitEquipment';
import type { SubjobVM } from 'ab-viewModels/workRequest/jobUpsert.viewModel';

import { bemElement } from 'af-utils/bem.util';

import { Button } from '@acceligentllc/storybook';

import type { TimeSplitEntryFormModel } from '../formModel';
import TimeSplitEntry from './TimeSplitEntry';
import { TimeSplitEntryAction } from '../TimeSheetEditModalBody';

type TimeSplitEntryEquipmentSections = {
	title: string;
	options: EquipmentListItemVM[];
};

export interface OwnProps {
	readonly: boolean;
	index: number;
	isAllowedToAddEquipment: boolean;
	canEditClassificationCodes: boolean;
	canSeeTimeAllocations: boolean;
	classificationCodeIdsWithValues: Record<number, string>;
	equipmentIdsWithValues: Record<number, string>;
	classificationCodes: FieldWorkClassificationCodeListItemVM[];
	sections: Nullable<TimeSplitEntryEquipmentSections>[];
	subJobs: SubjobVM[];
	changeEntries: (action: TimeSplitEntryAction, entryIndex: Nullable<number>) => void;
	change: (fieldName: string, value) => void;
	shouldRoundTimeIntervalsTo1Min: boolean;
	canSeeClassificationCode: boolean;
}

type Props = OwnProps & WrappedFieldArrayProps<TimeSplitEntryFormModel>;

const TimeSplitEntriesList: React.FC<Props> = (props) => {

	const {
		fields,
		isAllowedToAddEquipment,
		classificationCodeIdsWithValues,
		classificationCodes,
		sections,
		equipmentIdsWithValues,
		canEditClassificationCodes,
		changeEntries,
		change,
		readonly,
		subJobs,
		canSeeTimeAllocations,
		shouldRoundTimeIntervalsTo1Min,
		canSeeClassificationCode,
	} = props;

	const removeEntry = React.useCallback((index: number) => {
		changeEntries(TimeSplitEntryAction.REMOVE, index);
	}, [changeEntries]);

	const addEntry = React.useCallback(() => {
		changeEntries(TimeSplitEntryAction.ADD, null);
	}, [changeEntries]);

	const renderTimeSplitEntry = React.useCallback((field: string, index: number) => {
		const entry = fields.get(index);
		return (
			<Field
				canEditClassificationCodes={canEditClassificationCodes}
				canSeeClassificationCode={canSeeClassificationCode}
				canSeeTimeAllocations={canSeeTimeAllocations}
				change={change}
				changeEntries={changeEntries}
				classificationCodeIdsWithValues={classificationCodeIdsWithValues}
				classificationCodes={classificationCodes}
				component={TimeSplitEntry}
				entry={entry}
				equipmentIdsWithValues={equipmentIdsWithValues}
				index={index}
				key={index}
				name={field}
				readonly={readonly}
				removeEntry={removeEntry}
				sections={sections}
				shouldRoundTimeIntervalsTo1Min={shouldRoundTimeIntervalsTo1Min}
				subJobs={subJobs}
			/>
		);
	}, [
		canEditClassificationCodes,
		canSeeTimeAllocations,
		change,
		changeEntries,
		classificationCodeIdsWithValues,
		classificationCodes,
		equipmentIdsWithValues,
		fields,
		removeEntry,
		sections,
		readonly,
		subJobs,
		shouldRoundTimeIntervalsTo1Min,
		canSeeClassificationCode,
	]);

	return (
		<>
			<div className={bemElement('time-sheet-list__equipment-modal', 'time-split-list')}>
				{fields.map(renderTimeSplitEntry)}
			</div>
			{isAllowedToAddEquipment &&
				<Row className={bemElement('time-sheet-list__equipment-modal', 'add-equipment')}>
					<Button
						icon="ActionPlusSmall"
						label="Add Equipment"
						onClick={addEntry}
						style="link"
					/>
				</Row>
			}
		</>
	);
};

export default React.memo(TimeSplitEntriesList);
